import {
    AssetCategory,
    AssetClass,
    AssetJurisdiction,
    AssetRegulation,
    AssetStatus,
    AssetSubStatus,
    SpvLegalForm,
    StockType,
    TransferRestriction,
} from '@src/enums'
import { Contract, Issuer, Platform, SmAssetRisk } from '@src/types'
import { PaymentAccount } from './PaymentAccount'

export class Asset {
    asset_uid: string | null = null
    asset_location_city: string | null = null
    asset_location_state: string | null = null
    asset_location_country: string | null = null
    clearing_system: 'CI' | 'CF' | 'EMISSION' | 'STOCK_EMISSION' | null = null
    asset_title: string | null = null
    platform: Platform | null = null
    platform_name: string | null = null
    issuer_id: string | null = null
    issuer: Issuer | null = null
    category: AssetCategory | null = null
    funding_min: number | null = 0
    investment_min: number | null = 0
    currency: string | null = null
    investment_max: number | null = 0
    funding_max: number | null = 0
    iban: string | null = null
    bic: string | null = null
    bank_account_holder: string | null = null
    step_level: number | null = null
    quick_amounts: number[] = []
    contract_period: string | null = null
    funding_count: number | null = null
    funding_total: number | null = null
    base_funding_count: number | null = null
    base_funding_total: number | null = null
    manual_funding_totals = false
    deactivate_payment_account = false
    tags: string | null = null
    installment_investment = false
    installment_payout = false
    minimum_initial_payment: number | null = null
    maximum_initial_payment: number | null = null
    initial_payment_step: number | null = null
    minimum_installment: number | null = null
    maximum_installment: number | null = null
    installment_step: number | null = null
    minimum_monthly_rates: number | null = null
    maximum_monthly_rates: number | null = null
    minimum_yearly_rates: number | null = null
    maximum_yearly_rates: number | null = null
    interest_rate: number | null = null
    target_yield: string | null = null
    contract_range: string | null = null
    stripe_credentials: StripeCredentials | null = null
    payment_methods: PaymentMethods = new PaymentMethods()
    signature_manual = false
    signature_manual_confirmation_required = true
    signature_sproof = false
    signature_signteq = false
    require_qualified_signature = false
    require_advanced_qualified_signature = false
    asset_documents: AssetDocument[] = []
    linked_platform_documents: string[] = []
    signature_legal_notice: string | null = null
    emission_signature_document: EmissionSignatureDocument | null = null
    contract: Contract | null = null
    voucher_contract: Contract | null = null
    contracts: Contract[] = []
    created_on: string | null = null
    updated_at: string | null = null
    status: AssetStatus | null = null
    sub_status: AssetSubStatus | null = null
    has_nyala_project = false
    price_per_stock: number | null = null
    nominal_value: number | null = 1
    agio: number | null = 0
    minimum_investment: number | null = null
    stock_increments: number | null = null
    number_of_stocks: number | null = null
    maximum_total_investment_value: number | null = null
    stock_type: StockType | null = null
    isin: string | null = null
    voting_rights_per_share: number | null = null
    stock_emission_contract: Contract | null = null
    zoho_sync_enabled = false
    kyc_provider: 'signteq' | 'idnow' | null = null
    signature_feature_active = false
    sign_all_documents_at_once = false
    signature_signteq_subject: string | null = null
    payment_account_id: string | null = null
    payment_account: PaymentAccount | null = null
    payment_account_type: string | null = null
    deposit_account_holder_address: string | null = null
    deposit_account_holder_country: string | null = null
    hide_payment_info = false
    revoke_accrual = false
    sm_marketplace = false
    sm_trading_enabled = false
    sm_payment_confirmation_deadline: number | null = null
    sm_payment_fulfillment_deadline: number | null = null
    sm_signing_deadline: number | null = null
    sm_escalation_emails: string[] = []
    sm_required_base_data_fields: string[] = []
    sm_target_price: number | null = null
    sm_tolerance_band: number | null = null
    sm_subscription_ratio: number | null = null
    sm_exercise_campaign: string | null = null
    sm_transaction_fee: number | null = 0
    sm_template: ContractTemplate | null = null
    sm_asset_risks: SmAssetRisk[] = []
    sm_sproof_signature_type: string | null = null
    asset_class: AssetClass | null = null
    asset_regulation: AssetRegulation | null = null
    asset_jurisdiction: AssetJurisdiction[] | null = null
    is_subordinated = false
    is_tied_agent = false
    return_calculator_active = false
    transferable = false
    transfer_restriction: TransferRestriction | null = null
    pre_money_valuation: number | null = null
    equity_percentage: number | null = null
    dividend_preference_pct: number | null = null
    cooperative_registry_number: string | null = null
    cooperative_statute_url: string | null = null
    cooperative_notice_period: string | null = null
    cooperative_additional_contribution = false
    cooperative_mandatory_shares: number | null = null
    spv_legal_form: SpvLegalForm | null = null
    spv_participation_quota_pct: number | null = null
    spv_participation_exemption = false
    spv_corporate_tax_rate: number | null = null
    spv_lead_investor: string | null = null
}

export class AssetDocument {
    id: string | null = null
    name: string | null = null
    description: string | null = null
    url: string | null = null
    type: number | null = null
    upload_key: string | null = null
    language: string | null = 'de'
}

export interface ContractTemplate {
    id?: string
    name: string
    url: string
    upload_key: string
    custom_properties?: Record<string, any>
}

export class EmissionSignatureDocument {
    id: string | null = null
    uuid: string | null = null
    name: string | null = null
    signature_legal_notice: string | null = null
    url: string | null = null
    type: number | null = null
    upload_key: string | null = null
    lang: string | null = null
    created_on: string | null = null
}

export class PaymentMethods {
    credit_card = false
    wire_transfer = false
}

export class StripeCredentials {
    publishable_key: string | null = null
    secret_key: string | null = null
}

export interface NyalaRegistryExtractItem {
    external_customer_id: string
}
